import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useCompanyDocumentosFiscaisHooks } from './hooks';
import { CompanyDocumentoFiscalFormDialog } from './CompanyDocumentoFiscalFormDialog';
import {
  makeDocumentoFiscalId,
  type CompanyDocumentoFiscalDto,
  type CompanyDocumentoFiscalListItem,
} from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'modelo',
  sortOrder: 'asc',
};

export interface CompanyDocumentosFiscaisManagerProps {
  companyId: number;
  /** Se true, oculta CST/tipoRetencao PIS-COFINS no form (não se aplicam). */
  simplesNacional?: boolean;
}

/**
 * Núcleo reusável do CRUD de documentos fiscais — grid + form + delete.
 * Pattern espelha `CompanyAddressesManager`. PK composta `(modelo, serie)`
 * vira string `"<modelo>/<serie>"` para o hooks/api/delete.
 */
export function CompanyDocumentosFiscaisManager({
  companyId,
  simplesNacional = false,
}: CompanyDocumentosFiscaisManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('documentos-fiscais', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<CompanyDocumentoFiscalDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<CompanyDocumentoFiscalListItem | null>(null);

  const hooks = useCompanyDocumentosFiscaisHooks(companyId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: CompanyDocumentoFiscalListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(makeDocumentoFiscalId(row.modelo, row.serie)));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(makeDocumentoFiscalId(pendingDelete.modelo, pendingDelete.serie));
      toast.success(t('features.empresa.documentosFiscais.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<CompanyDocumentoFiscalListItem>[] = [
    {
      field: 'modelo',
      headerName: t('features.empresa.documentosFiscais.columns.modelo'),
      width: 100,
    },
    {
      field: 'serie',
      headerName: t('features.empresa.documentosFiscais.columns.serie'),
      width: 120,
    },
    {
      field: 'descricaoNF',
      headerName: t('features.empresa.documentosFiscais.columns.descricaoNF'),
      width: 160,
      sortable: false,
      renderCell: (params) =>
        params.row.descricaoNF
          ? t(`features.empresa.documentosFiscais.descricaoNF.${params.row.descricaoNF}`)
          : '—',
    },
    {
      field: 'ambienteNF',
      headerName: t('features.empresa.documentosFiscais.columns.ambienteNF'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          variant={params.row.ambienteNF === 1 ? 'filled' : 'outlined'}
          color={params.row.ambienteNF === 1 ? 'success' : 'default'}
          label={
            params.row.ambienteNF === 1
              ? t('features.empresa.documentosFiscais.ambienteNF.producao')
              : t('features.empresa.documentosFiscais.ambienteNF.homologacao')
          }
        />
      ),
    },
    {
      field: 'tipoIntegracaoNF',
      headerName: t('features.empresa.documentosFiscais.columns.tipoIntegracaoNF'),
      width: 200,
      sortable: false,
      renderCell: (params) =>
        params.row.tipoIntegracaoNF
          ? t(`features.empresa.documentosFiscais.tipoIntegracaoNF.${params.row.tipoIntegracaoNF}`)
          : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.empresa.documentosFiscais.actions.edit', {
                modelo: params.row.modelo,
                serie: params.row.serie,
              })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.empresa.documentosFiscais.actions.delete', {
                modelo: params.row.modelo,
                serie: params.row.serie,
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.empresa.documentosFiscais.actions.new')}
        </Button>
      </Stack>

      <DataTable<CompanyDocumentoFiscalListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.empresa.documentosFiscais.searchPlaceholder')}
      />

      <CompanyDocumentoFiscalFormDialog
        open={editing !== null}
        documento={editing === 'new' ? null : editing}
        simplesNacional={simplesNacional}
        hooks={{
          useCreate: hooks.useCreate,
          useUpdate: hooks.useUpdate,
        }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.empresa.documentosFiscais.deleteConfirm.title')}
        description={t('features.empresa.documentosFiscais.deleteConfirm.description', {
          modelo: pendingDelete?.modelo ?? '',
          serie: pendingDelete?.serie ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
